import { useMemo } from 'react';
import semver from 'semver';
import { Cluster } from 'api/models';
import { K8S_VERSIONS } from 'constants/kubernetes-versions';
import { SUPPORT_LEVEL_MAP } from 'constants/kubernetes-support-map';
import { getClusterProvider } from 'utils/clusterStates';

function getK8sVersionsForProvider(provider: string): string[] {
  if (provider in SUPPORT_LEVEL_MAP) {
    return Object.keys(
      SUPPORT_LEVEL_MAP[provider as keyof typeof SUPPORT_LEVEL_MAP],
    );
  }
  return K8S_VERSIONS;
}

// FilterMode determines the mode of filtering the available K8s versions
// 'next-minor-only': list current and next minor versions
// 'greater-than-current': list current minor version, and all minor versions released after it
type FilterMode = 'next-minor-only' | 'greater-than-current';

interface useAvailableK8sVersionsForUpgradeOptions {
  filterMode?: FilterMode;
}

// useAvailableK8sVersionsForUpgrade returns target K8s versions for a cluster to upgrade to, based on its provider
export function useAvailableK8sVersionsForUpgrade(
  cluster: Cluster | undefined,
  options: useAvailableK8sVersionsForUpgradeOptions = {},
): string[] {
  const { filterMode = 'greater-than-current' } = options;

  return useMemo(() => {
    if (!cluster) return [];

    const provider = getClusterProvider(cluster);
    const versions = getK8sVersionsForProvider(provider);

    const coercedVersion = semver.coerce(cluster.version);
    if (!coercedVersion) return [];

    if (filterMode === 'next-minor-only') {
      const currentMinor = semver.minor(coercedVersion);
      const currentMajor = semver.major(coercedVersion);

      return versions.filter((v) => {
        const [major, minor] = v.split('.').map(Number);
        return (
          major === currentMajor &&
          (minor === currentMinor || minor === currentMinor + 1)
        );
      });
    }

    return versions.filter((v) => semver.lt(coercedVersion, `${v}.99`));
  }, [cluster?.id, cluster?.version, filterMode]);
}
